import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, getToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/signature-pad";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { Trash2, PenTool } from "lucide-react";

interface SavedSignature {
    id: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    createdAt: string;
}

/**
 * Self-service saved-signature panel backed by /api/me/signature. When a
 * signature is saved here, the form-fill engine automatically uses it on
 * every backflow test / work order the tech signs, without forcing them to
 * re-sign at the end of each workflow.
 */
export function SavedSignatureCard() {
    const qc = useQueryClient();

    const sigQuery = useQuery({
        queryKey: ["me-signature"],
        queryFn: async (): Promise<SavedSignature | null> => {
            try {
                const res = await api.get<{ signature: SavedSignature }>("/api/me/signature");
                return res.signature ?? null;
            } catch (err) {
                if (err instanceof ApiError && err.status === 404) return null;
                throw err;
            }
        },
    });

    const [pendingDataUrl, setPendingDataUrl] = useState<string | null>(null);

    // The /api/me/signature/file endpoint streams the bytes — we render
    // it inside an <img> so the existing JWT-based auth is in play
    // (browser sends the cookie/header automatically for same-origin).
    // Append the token as a query so new tabs / iframes that don't
    // carry the Authorization header still load the preview. We also
    // bust the cache on the saved-at timestamp so a fresh save shows
    // the new image immediately.
    const previewUrl = useMemo(() => {
        if (!sigQuery.data) return null;
        const token = getToken();
        const stamp = encodeURIComponent(sigQuery.data.createdAt);
        return `/api/me/signature/file?v=${stamp}${token ? `&token=${encodeURIComponent(token)}` : ""}`;
    }, [sigQuery.data]);

    const saveMutation = useMutation({
        mutationFn: (dataUrl: string) => saveMySignature(dataUrl),
        onSuccess: () => {
            setPendingDataUrl(null);
            qc.invalidateQueries({ queryKey: ["me-signature"] });
            toast.success("Saved signature updated");
        },
        onError: () => toast.error("Failed to save signature"),
    });

    const clearMutation = useMutation({
        mutationFn: () => api.delete<void>("/api/me/signature"),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["me-signature"] });
            toast.success("Saved signature cleared");
        },
        onError: () => toast.error("Failed to clear saved signature"),
    });

    const hasSaved = !!sigQuery.data && !pendingDataUrl;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <PenTool className="h-4 w-4" /> Saved signature
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Sign once and we keep it on file. Your saved signature is used automatically anywhere your tech
                    signature is required — backflow jurisdiction forms (TCEQ-20700, etc.), work orders, and reports. If
                    you sign at the end of a workflow, that per-job signature wins; otherwise this saved one fills the
                    signature line.
                </p>

                {sigQuery.isLoading ? (
                    <p className="text-sm text-foreground/60">Loading…</p>
                ) : (
                    <>
                        {hasSaved && previewUrl && (
                            <div className="space-y-3">
                                <div className="rounded-lg border border-border bg-white p-3 flex items-center justify-center">
                                    <img
                                        src={previewUrl}
                                        alt="Your saved signature"
                                        className="max-h-32 object-contain"
                                    />
                                </div>
                                <p className="text-xs text-foreground/80">
                                    Saved {new Date(sigQuery.data!.createdAt).toLocaleString()}
                                </p>
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" onClick={() => setPendingDataUrl("")}>
                                        <PenTool className="h-4 w-4 mr-1.5" />
                                        Replace
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="text-destructive hover:bg-destructive/10"
                                        disabled={clearMutation.isPending}
                                        onClick={async () => {
                                            if (
                                                await confirmDialog({
                                                    message: "Clear your saved signature?",
                                                    destructive: true,
                                                    confirmLabel: "Clear",
                                                })
                                            ) {
                                                clearMutation.mutate();
                                            }
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1.5" />
                                        {clearMutation.isPending ? "Clearing…" : "Clear"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {(!hasSaved || pendingDataUrl !== null) && (
                            <div className="space-y-3">
                                <SignaturePad
                                    onSave={(dataUrl) => saveMutation.mutate(dataUrl)}
                                    width={500}
                                    height={180}
                                />
                                {pendingDataUrl !== null && (
                                    <Button type="button" variant="ghost" onClick={() => setPendingDataUrl(null)}>
                                        Cancel
                                    </Button>
                                )}
                                {saveMutation.isPending && <p className="text-xs text-foreground/80">Saving…</p>}
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * PUT a drawn signature (a `data:image/png;base64,…` URL) as the signed-in
 * user's saved tester signature. The only writer is `PUT /api/me/signature`;
 * this card and the field app's signature pad (#1608) both call it.
 */
export function saveMySignature(dataUrl: string) {
    // Split and rebuild as a Blob so it multipart-uploads with the same
    // helper used by attachments.
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/data:(.*?);/)?.[1] ?? "image/png";
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });

    const fd = new FormData();
    fd.append("file", blob, "signature.png");
    return api.putForm<{ signature: SavedSignature }>("/api/me/signature", fd);
}
